import type { TadaDocumentNode } from 'gql.tada';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import {
  clearQueuedEventListReset,
  type EventListResetScope,
  useQueuedEventListResetDate,
} from '@/client/exchanges/cacheExchange/eventListResetQueue';
import useDateCursorBidirectionalList from '@/hooks/useDateCursorBidirectionalList';

type EventListSessionScope = EventListResetScope;

type DateCursorItem = {
  datetime?: string | null;
  id: string;
};

type DateCursorVariables = {
  limit: number;
  where: Record<string, unknown>;
};

type UseEventListSessionOptions<
  TItem extends DateCursorItem,
  TFutureData,
  TPastData,
  TVariables extends DateCursorVariables,
> = {
  baseWhere: Record<string, unknown>;
  buildVariables?: (args: DateCursorVariables) => TVariables;
  directionLimit?: number;
  fieldName: string;
  futureQuery: TadaDocumentNode<TFutureData, TVariables>;
  pastQuery: TadaDocumentNode<TPastData, TVariables>;
  pause?: boolean;
  resetScope: EventListSessionScope;
  selectFutureItems(data: TFutureData | undefined): readonly TItem[];
  selectPastItems(data: TPastData | undefined): readonly TItem[];
};

export default function useEventListSession<
  TItem extends DateCursorItem,
  TFutureData,
  TPastData,
  TVariables extends DateCursorVariables = DateCursorVariables,
>({
  baseWhere,
  buildVariables,
  directionLimit,
  fieldName,
  futureQuery,
  pastQuery,
  pause,
  resetScope,
  selectFutureItems,
  selectPastItems,
}: UseEventListSessionOptions<TItem, TFutureData, TPastData, TVariables>) {
  const [session, setSession] = useState(() => ({
    anchorDate: DateTime.local().toISO(),
    key: 0,
  }));
  const { anchorDate } = session;
  const queuedTransitionRef = useRef<string | undefined>(undefined);
  const transitionIdRef = useRef(0);

  const scopeKind = resetScope.kind;
  const scopeEventId =
    resetScope.kind === 'detail' ? resetScope.eventId : undefined;
  const queuedJumpDate = useQueuedEventListResetDate(resetScope);

  const bidirectionalList = useDateCursorBidirectionalList({
    anchorValue: anchorDate,
    baseWhere,
    buildVariables,
    directionLimit,
    fieldName,
    futureQuery,
    pastQuery,
    pause,
    selectFutureItems,
    selectPastItems,
  });

  const resetSessionRef = useRef(bidirectionalList.resetSession);

  useEffect(() => {
    resetSessionRef.current = bidirectionalList.resetSession;
  }, [bidirectionalList.resetSession]);

  useEffect(() => {
    if (!queuedJumpDate || queuedTransitionRef.current === queuedJumpDate) {
      return;
    }

    queuedTransitionRef.current = queuedJumpDate;
    const transitionId = transitionIdRef.current + 1;
    transitionIdRef.current = transitionId;

    const transitionToQueuedDate = async () => {
      await resetSessionRef.current(queuedJumpDate);

      if (transitionIdRef.current !== transitionId) {
        return;
      }

      setSession({ anchorDate: queuedJumpDate, key: transitionId });
      if (scopeKind === 'detail') {
        if (!scopeEventId) {
          return;
        }

        clearQueuedEventListReset({
          eventId: scopeEventId,
          kind: 'detail',
        });
      } else {
        clearQueuedEventListReset({
          kind: 'home',
        });
      }
      queuedTransitionRef.current = undefined;
    };

    void transitionToQueuedDate();
  }, [queuedJumpDate, scopeEventId, scopeKind]);

  const refresh = async () => {
    const nextAnchorDate = DateTime.local().toISO();
    const transitionId = transitionIdRef.current + 1;
    transitionIdRef.current = transitionId;

    await resetSessionRef.current(nextAnchorDate);

    if (transitionIdRef.current !== transitionId) {
      return;
    }

    setSession({ anchorDate: nextAnchorDate, key: transitionId });
  };

  const fetching =
    bidirectionalList.isInitialFetching || bidirectionalList.isListFetching;

  return {
    anchorDate,
    data: bidirectionalList.listData,
    fetchNextPage: bidirectionalList.fetchNextPage,
    fetchPreviousPage: bidirectionalList.fetchPreviousPage,
    fetching,
    listKey: session.key,
    refresh,
  };
}
